
import React, { useState, useEffect, useMemo } from "react";
import {
    AppBar,
    Toolbar,
    Container,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@material-ui/core";
import { makeStyles, MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";
import Axios from "axios";
import { getBaseURL } from "../baseURL";

const theme = createMuiTheme({
    typography: {
        useNextVariants: true,
        fontFamily: "Arial, Helvetica, sans-serif",
    },
    palette: {
        primary: {
            main: "#140a9a"
        },
        secondary: {
            main: "#818386"
        }
    },
    overrides: {
        MuiTableRow: {
            root: { //for the body
                height: "100%"
            },
            head: { //for the head
                height: "100%"
            },
        }
    },
    drawerWidth: 360,
});

/* ---------------- Styles ---------------- */
const useStyles = makeStyles(theme => ({
    root: {
        padding: theme.spacing(1, 2),

        width: '100%',
        maxWidth: 'none',
        margin: 0,

    },


    usersCell: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.4,
        maxWidth: 360, // optional: constrain width so the wrap is visible
    },



    cellWrap: {
        maxWidth: 10,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    },

    table: {
        width: '100%',
        tableLayout: 'fixed',
        borderCollapse: 'collapse',
    },
    cell: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },

    smallTable: {
        fontSize: "0.8rem",
        marginTop: 16,
        "& *": {
            fontSize: "0.8rem !important",
        }
    },


    fileRow: {
        '&:hover': {
            backgroundColor: `#E8E8E8`,
            border: `1px solid ${theme.palette.primary.main}`,
        }
    }

}));




/* ---------------- Helpers ---------------- */


/**
 * Extract BOTH requestId (r) and token (t) from URL.
 * Supports:
 *   - Proper query string:  /managerapproval?r=123&t=ABC
 *   - Fallback (malformed path): /managerapproval/r=123&t=ABC
 */
export function getTokenAndRequestFromUrl() {
    try {
        const url = new URL(window.location.href);

        // 1) Proper query params
        const qRequestIdStr = url.searchParams.get('r') || url.searchParams.get('requestId');
        const qToken = url.searchParams.get('t') || url.searchParams.get('token');
        const qRequestId = qRequestIdStr != null ? parseInt(qRequestIdStr, 10) : null;
        console.log(qRequestId);
        const validQRequestId = Number.isFinite(qRequestId) ? qRequestId : null;

        if (qRequestIdStr || qToken) {
            return {
                requestId: validQRequestId,
                token: qToken || null
            };
        }

        // 2) Fallback: treat extra path segments as pseudo query (handles /managerapproval/r=123&t=ABC)
        const parts = url.pathname.split('/').filter(Boolean);
        const idx = parts.findIndex(p => p.toLowerCase() === 'managerapproval');
        if (idx >= 0 && parts[idx + 1]) {
            // IMPORTANT: join with '&' (not '&amp;')
            const pseudo = parts.slice(idx + 1).join('&');
            const fake = new URL(`http://x/?${pseudo}`);
            const pRequestIdStr = fake.searchParams.get('r') || fake.searchParams.get('requestId');
            const pToken = fake.searchParams.get('t') || fake.searchParams.get('token');
            const pRequestId = pRequestIdStr != null ? parseInt(pRequestIdStr, 10) : null;
            const validPRequestId = Number.isFinite(pRequestId) ? pRequestId : null;

            return {
                requestId: validPRequestId,
                token: pToken || null
            };
        }

        return { requestId: null, token: null };
    } catch {
        return { requestId: null, token: null };
    }
}

/* ---------------- GraphQL ---------------- */
const GET_FILES = gql`
  query GetFiles($approved: Boolean) {
    file(approved: $approved) {
      id
      companyId
      fileName
      dateUploaded
      expirationDate
      approved
      company { name }     
    }
  }
`;




export const ManagerApproval = () => {
    const classes = useStyles();


    // --- URL token & requestId
    const [{ requestId, token }, setUrlParams] = useState({ requestId: null, token: null });

    // --- Token validation state (via axios POST /files/Validate)
    const [validation, setValidation] = useState({
        loading: true,
        ok: false,
        statusCode: null,
        message: 'Validating link…',
        requestId: null,
        requesterEmail: null,
        companyName: null,
        companyId: null,
        expiresOnUtc: null,
        currentStatus: null,
        fileId: null, // map to Files.id if your API includes it
    });

    // Extract URL params once
    useEffect(() => {
        const { requestId, token } = getTokenAndRequestFromUrl();
        setUrlParams({ requestId, token });
    }, []);

    const guestHeader =
        token
            ? { "x-approval-token": token }
            : {};
    const config = {
        headers: guestHeader,
        timeout: 240000,
    };

    // Validate via backend as soon as we have both values
    useEffect(() => {
        let cancelled = false;

        async function runValidate() {
            if (requestId == null || !token) {
                setValidation(v => ({
                    ...v,
                    loading: false,
                    ok: false,
                    statusCode: 400,
                    message: 'Missing requestId or token in URL.',
                }));
                return;
            }

            setValidation(v => ({ ...v, loading: true, message: 'Validating link…' }));

            const payload = {
                RequestId: requestId,
                Token: token,
                BaseUrl: getBaseURL(),
            };


            try {
                const res = await Axios.post(`${getBaseURL()}files/Validate`, payload, config

                );
                console.log('[Validate] response:', { status: res.status, data: res.data });
                if (cancelled) return;

                const data = res.data || {};
                setValidation({
                    loading: false,
                    ok: true,
                    statusCode: res.status,
                    message: data?.message || 'Link validated.',
                    requestId: data?.requestId ?? requestId,
                    requesterEmail: data?.requesterEmail ?? null,
                    companyName: data?.companyName ?? null,
                    companyId: data?.companyId ?? null,
                    expiresOnUtc: data?.expiresOnUtc ?? null,
                    currentStatus: data?.currentStatus ?? 'Pending',
                    fileId: data?.fileId ?? null,
                });
            } catch (err) {
                if (cancelled) return;

                const status = err?.response?.status || 500;
                const payload = err?.response?.data;

                // Backend codes could be:
                // 400 BadRequest: invalid/expired
                // 409 Conflict: used
                // 410 Gone: expired
                let msg = 'Link validation failed.';
                if (payload?.Message) msg = payload.Message;
                else if (typeof payload === 'string') msg = payload;

                setValidation({
                    loading: false,
                    ok: false,
                    statusCode: status,
                    message: msg,
                    requestId,
                    requesterEmail: null,
                    companyName: null,
                    companyId: null,
                    expiresOnUtc: null,
                    currentStatus: (payload && payload.Status) || null,
                    fileId: null,
                });
            }
        }

        runValidate();
        return () => { cancelled = true; };
    }, [requestId, token]);



    const [filesLoading, setFilesLoading] = useState(false);
    const [filesError, setFilesError] = useState(null);
    const [filedata, setData] = useState(null);


    // Build REST API URL
    const filesUrl = `${getBaseURL()}files/getFileById/${requestId}`;

    // Load files only after validation OK (optional: you can always load them)

    useEffect(() => {
        if (!validation.ok) return;   // wait for token validation

        async function loadFiles() {
            try {
                setFilesLoading(true);
                setFilesError(null);


                Axios.get(filesUrl, config)
                    .then((response) => {
                        console.log("API Response:", response.data);

                        setData(response.data || null);
                    })
                    .catch((err) => {
                        console.error("Failed to load file:", err);

                    })

            } catch (err) {
                console.error("Files load failed", err);
                setFilesError(err);
            } finally {
                setFilesLoading(false);
            }
        }

        loadFiles();
    }, [validation.ok, token]);


    const [files, setFiles] = useState([]);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectError, setRejectError] = useState(false);



    const breakAtAt = (s = '') => s.replace('@', '\n@');

    // Actions
    const onApproveClick = async (approved, reason = null) => {
        setData(prev => prev ? { ...prev, loading: true, error: false } : prev);

        try {
            const payload = {

                FileId: requestId,
                ManagerApproved: approved,
                RejectionReason: approved ? null : reason, // added
                UserEmail: filedata?.email ?? null,
            };


            const res = await Axios.post(`${getBaseURL()}files/approveFile`, payload, config);


            setData(prev => ({
                ...(prev || {}),
                ...(res?.data || {}),
                processed: true,
                managerApproved: approved,
                loading: false,
                error: false,
            }));

            console.log(filedata);


        } catch (error) {

            setData(prev => prev ? {
                ...prev,
                loading: false,
                error: true,

            } : prev);
        }


    };

    return (
        <Container maxWidth={false} disableGutters={true} className={classes.root}>
            <Typography variant="h5">Manager Approval</Typography>

            {/* Link / token validation status */}
            {validation.loading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <CircularProgress size={20} />
                    <Typography variant="subtitle1">{validation.message}</Typography>
                </div>
            ) : !validation.ok ? (
                <Typography variant="subtitle1" color="error" style={{ marginTop: 12 }}>
                    {validation.message}
                </Typography>
            ) : (
                <div style={{ marginTop: 12 }}>
                    {/* Summary line when validation succeeds */}
                    <Typography variant="subtitle1" style={{ marginBottom: 6 }}>
                        Link verified
                        {validation.expiresOnUtc ? ` • Expires: ${new Date(validation.expiresOnUtc).toLocaleString()}` : ''}.
                        {validation.currentStatus ? ` Status: ${validation.currentStatus}.` : ''}
                    </Typography>
                    {validation.requesterEmail && (
                        <Typography variant="body2">Requester: {validation.requesterEmail}</Typography>
                    )}
                    {validation.companyName && (
                        <Typography variant="body2">Company: {validation.companyName}</Typography>
                    )}
                </div>
            )}

            {/* Files (gated until validation OK) */}
            {validation.ok && (filesLoading ? (
                <div style={{ marginTop: 16 }}>
                    <CircularProgress />
                </div>
            ) : filesError ? (
                <Typography variant="subtitle1" color="error" style={{ marginTop: 12 }}>
                    Failed to load files.
                </Typography>
            ) : filedata ? (
                <TableContainer className={classes.smallTable}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>File Name</TableCell>
                                <TableCell>Original File Names</TableCell>
                                <TableCell>Date Added</TableCell>
                                <TableCell>Added By</TableCell>
                                <TableCell>For Company</TableCell>
                                <TableCell>Justification</TableCell>
                                <TableCell>Selected Users</TableCell>

                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>

                            <TableRow key={requestId} className={classes.fileRow}>
                                <TableCell>{filedata.fileName}</TableCell>
                                <TableCell>
                                    {String(filedata.originalFileNames || '')
                                        .split(';')
                                        .map((u, idx) => {
                                            const t = u.trim();
                                            if (!t) return null;
                                            return <div key={idx}>{t}</div>;
                                        })}
                                </TableCell>
                                <TableCell>{filedata.dateUploaded}</TableCell>
                                <TableCell>{breakAtAt(filedata.email)}</TableCell>
                                <TableCell>{filedata.companyName}</TableCell>
                                <TableCell className={classes.usersCell}>{filedata.justification}</TableCell>

                                <TableCell>
                                    {String(filedata.selectedUsers || '')
                                        .split(';')
                                        .map((u, idx) => {
                                            const t = u.trim();
                                            if (!t) return null;
                                            return <div key={idx}>{t}</div>;
                                        })}
                                </TableCell>


                                <TableCell>
                                    {filedata.loading ? (
                                        <CircularProgress size={20} />
                                    ) : filedata.processed ? (
                                        filedata.managerApproved ? (
                                            <Typography variant="subtitle1">APPROVED</Typography>
                                        ) : (
                                            <Typography variant="subtitle1">REJECTED</Typography>
                                        )
                                    ) : (
                                        <>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                style={{ marginRight: '10px' }}
                                                onClick={() => onApproveClick(true)}
                                                disabled={!validation.ok || filedata?.loading}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => setRejectOpen(true)}
                                                disabled={!validation.ok || filedata?.loading}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>

                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="subtitle1" style={{ marginTop: 12 }}>
                    No Files Available
                </Typography>
            ))}
            <Dialog
        open={rejectOpen}
        onClose={() => {
          setRejectOpen(false);
          setRejectReason("");
          setRejectError(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>File Rejection Reason</DialogTitle>

        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            Please provide a reason for rejection.
          </Typography>

          <TextField
            autoFocus
            label="Rejection Reason"
            multiline
            rows={4}
            fullWidth
            value={rejectReason}
            error={rejectError}
            helperText={rejectError ? "Rejection reason is required" : ""}
            onChange={(e) => {
              setRejectReason(e.target.value);
              setRejectError(false);
            }}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setRejectOpen(false);
              setRejectReason("");
              setRejectError(false);
            }}
          >
            Cancel
          </Button>

          <Button
            color="secondary"
            variant="contained"
            onClick={() => {
              if (!rejectReason.trim()) {
                setRejectError(true);
                return;
              }
              setRejectOpen(false);
              onApproveClick(false, rejectReason); // ✅ send reason
              setRejectReason("");
            }}
          >
            Submit Rejection
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
    );
};